import { yupResolver } from "@hookform/resolvers/yup";
import format from "date-fns/format";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../Button";
import FormDateInput from "../FormDateInput";
import FormTextInput from "../FormTextInput";
import Typography from "../Typography";
import {
    defaultSetCalculationFormValues,
    emissionsFactorFormValidationSchema,
    SetCalculationFormValues,
} from "./SetCalculationFormValues";
import FormDropdown from "../FormDropdown";
import { useActivityDataUnits } from "../../api/activity-data";

type SetCalculationFormProps = {
    locationName?: string;
    dataSourceName?: string;
    isLoading: boolean;
    isOpen?: boolean;
    onCancel: () => void;
    onSubmit: (values: SetCalculationFormValues) => void;
};

const SetCalculationForm = ({
    locationName,
    dataSourceName,
    isLoading,
    isOpen,
    onCancel,
    onSubmit,
}: SetCalculationFormProps) => {
    const [calculationFormValuesStore, setCalculationFormValuesStore] =
        useState<SetCalculationFormValues>();
    const methods = useForm<SetCalculationFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(emissionsFactorFormValidationSchema),
        defaultValues: defaultSetCalculationFormValues,
    });

    const { activityDataUnits, isLoading: isLoadingActivityDataUnits } =
        useActivityDataUnits();

    const activityDataUnitsOptions = activityDataUnits
        ? activityDataUnits.map((activityDataUnit) => ({
              value: activityDataUnit.id,
              label: activityDataUnit.suffix,
          }))
        : [];

    const handleFormSubmit = (values: SetCalculationFormValues) => {
        setCalculationFormValuesStore(values);
    };

    const handleCalculationConfirmation = async () => {
        if (calculationFormValuesStore) {
            await onSubmit(calculationFormValuesStore);
        }
    };

    const handleCalculationConfirmationCancellation = () => {
        setCalculationFormValuesStore(undefined);
    };

    const handleCloseSetCalculationForm = useCallback(() => {
        handleCalculationConfirmationCancellation();
        methods.reset();
    }, [methods]);

    useEffect(() => {
        // Resets form if/when parent dialog is closed
        if (typeof isOpen === "boolean" && !isOpen) {
            handleCloseSetCalculationForm();
        }
    }, [isOpen, handleCloseSetCalculationForm]);

    return (
        <>
            {!calculationFormValuesStore && (
                <form
                    onSubmit={methods.handleSubmit(handleFormSubmit)}
                    className="space-y-4 max-w-xl rounded-xl bg-surface-light-1 p-6 mx-auto"
                >
                    <div className="space-y-4">
                        <Controller
                            name="normalizedUnitsId"
                            control={methods.control}
                            render={({ field: { onChange, value } }) => (
                                <FormDropdown
                                    required
                                    isLoadingOptions={
                                        isLoadingActivityDataUnits
                                    }
                                    label="Normalized units"
                                    pleaseSelectText="Select normalized units"
                                    options={activityDataUnitsOptions}
                                    value={value}
                                    onChange={onChange}
                                    error={
                                        methods.formState.errors
                                            .normalizedUnitsId
                                    }
                                />
                            )}
                        />
                        <FormTextInput
                            required
                            type="number"
                            step="0.00001"
                            label="Conversion factor"
                            placeholder="Enter conversion factor"
                            helpText="e.g. 0.123"
                            min={0}
                            max={1000000}
                            {...methods.register("conversionFactor")}
                            error={methods.formState.errors.conversionFactor}
                        />
                        <FormTextInput
                            required
                            type="number"
                            step="0.00001"
                            label="Emissions factor"
                            placeholder="Enter emissions factor"
                            helpText="e.g. 0.123"
                            min={0}
                            max={1000000}
                            {...methods.register("emissionsFactor")}
                            error={methods.formState.errors.emissionsFactor}
                        />
                        <Controller
                            name="activeFrom"
                            control={methods.control}
                            render={({ field: { onChange, value } }) => (
                                <FormDateInput
                                    required
                                    id="applicable-from-date"
                                    label="Applicable from date"
                                    helperText="DD/MM/YYYY"
                                    onChange={onChange}
                                    value={value}
                                    error={methods.formState.errors.activeFrom}
                                />
                            )}
                        />
                    </div>
                    <div className="flex justify-end gap-4 mt-10">
                        <Button
                            variant="transparent"
                            type="button"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button>Create</Button>
                    </div>
                </form>
            )}

            {calculationFormValuesStore && (
                <div>
                    <Typography variant="body-large" component="p">
                        <b>{locationName}</b>
                    </Typography>
                    <Typography variant="body-large" component="p">
                        <b>{dataSourceName}</b>
                    </Typography>
                    <Typography variant="headline-large" component="p">
                        {calculationFormValuesStore.emissionsFactor}
                    </Typography>
                    <Typography
                        variant="body-large"
                        component="p"
                        className="mt-2"
                    >
                        <b>
                            Applicable from{" "}
                            {calculationFormValuesStore.activeFrom
                                ? format(
                                      calculationFormValuesStore.activeFrom,
                                      "dd/MM/yyyy"
                                  )
                                : ""}
                        </b>
                    </Typography>
                    <div className="flex justify-end gap-4 mt-10">
                        <Button
                            variant="transparent"
                            type="button"
                            onClick={handleCalculationConfirmationCancellation}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={isLoading}
                            onClick={handleCalculationConfirmation}
                        >
                            Confirm
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default SetCalculationForm;
